import { parseMermaid } from "./parseMermaid.js";
import { graphToExcalidraw } from "./graphToExcalidraw.js";
import { convertToExcalidrawElements } from "@excalidraw/excalidraw";
import { nanoid } from "nanoid";
import { wrapText } from "./converter/helpers.js";
import { applyRichTextFormatting } from "./richText.js";

export const parseMarkdownToExcalidraw = async (markdown: string, config: { fontSize: number } = { fontSize: 20 }) => {
  const lines = markdown.split(/\r?\n/);
  const elements: any[] = [];
  let currentY = 0;
  const DEFAULT_WIDTH = 600;
  const PADDING = 32;

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      currentY += 16;
      i++;
      continue;
    }

    // 1. Detect Mermaid Blocks
    if (trimmed.startsWith("```mermaid")) {
      let definition = "";
      i++;
      while (i < lines.length && !lines[i].trim().startsWith("```")) {
        definition += lines[i] + "\n";
        i++;
      }
      i++;

      try {
        const parsed = await parseMermaid(definition.trim());
        const result = graphToExcalidraw(parsed, { fontSize: config.fontSize });
        const diagramElements = convertToExcalidrawElements(result.elements).map((el: any) => ({ ...el }));
        
        let minY = Infinity;
        let maxY = -Infinity;
        diagramElements.forEach((el: any) => {
          if (el.points && el.points.length > 0) {
            const minP = Math.min(...el.points.map((p: any) => p[1]));
            const maxP = Math.max(...el.points.map((p: any) => p[1]));
            minY = Math.min(minY, el.y + minP);
            maxY = Math.max(maxY, el.y + maxP);
          } else {
            minY = Math.min(minY, el.y);
            maxY = Math.max(maxY, el.y + (el.height || 0));
          }
        });

        const diagramHeight = maxY - minY;
        const offset = currentY - minY;

        diagramElements.forEach((el: any) => {
          el.y += offset;
          elements.push(el);
        });

        currentY += diagramHeight + PADDING;
      } catch (e) {
        console.error("Failed to parse mermaid block", e);
      }
      continue;
    }

    // 2. Detect Generic Code Blocks (Monospace + Card)
    if (trimmed.startsWith("```")) {
      let codeContent = "";
      i++;
      while (i < lines.length && !lines[i].trim().startsWith("```")) {
        codeContent += lines[i] + "\n";
        i++;
      }
      i++;

      const fontSize = config.fontSize * 0.85;
      const linesArr = codeContent.trim().split("\n");
      const height = linesArr.length * fontSize * 1.5 + 24;
      const width = DEFAULT_WIDTH;

      // Card Background for Code
      elements.push(...convertToExcalidrawElements([
        {
          type: "rectangle",
          x: -10,
          y: currentY - 5,
          width: width + 20,
          height: height,
          strokeColor: "#E0E0E0",
          backgroundColor: "#F8F9FA",
          fillStyle: "solid",
          roundness: { type: 3 },
          opacity: 100,
        }
      ] as any));

      elements.push(...convertToExcalidrawElements([
        {
          type: "text",
          x: 5,
          y: currentY,
          width: width,
          text: codeContent.trim(),
          fontSize,
          fontFamily: 1,
          strokeColor: "#444444",
          textAlign: "left",
        }
      ] as any));

      currentY += height + PADDING;
      continue;
    }

    // 3. Detect Headings
    if (trimmed.startsWith("#")) {
      const match = trimmed.match(/^(#{1,6})\s+(.*)/);
      if (match) {
        const level = match[1].length;
        const text = cleanMarkdown(match[2]);
        const fontSize = config.fontSize * (2.2 - (level * 0.2));
        
        elements.push(...convertToExcalidrawElements([
          {
            type: "text",
            x: 0,
            y: currentY,
            text,
            fontSize,
            strokeColor: "#1A1A1A",
            textAlign: "left",
            fontFamily: 4,
          }
        ] as any));
        
        currentY += fontSize * 1.5 + PADDING;
        i++;
        continue;
      }
    }

    // 4. Detect Checklists and Lists (Improved Alignment)
    const listMatch = trimmed.match(/^([-*]|\d+\.)\s+(\[([ xX])\]\s+)?(.*)/);
    if (listMatch) {
      const isChecklist = !!listMatch[2];
      const isChecked = listMatch[3]?.toLowerCase() === "x";
      
      const rawListText = cleanMarkdown(listMatch[4]);
      const text = wrapMarkdownText(rawListText, 70); // Wrap list items at ~70 characters to fit well
      const fontSize = config.fontSize;
      
      const xOffset = isChecklist ? 40 : 30;
      
      const groupId = nanoid();
      const listItemElements: any[] = [];
      
      if (isChecklist) {
        listItemElements.push({
          type: "rectangle",
          x: 0,
          y: currentY + 4,
          width: 18,
          height: 18,
          strokeColor: isChecked ? "#00A878" : "#999999",
          backgroundColor: isChecked ? "#00A878" : "transparent",
          fillStyle: "solid",
          strokeWidth: 1,
          roundness: { type: 3 },
          groupIds: [groupId]
        });
        
        if (isChecked) {
          listItemElements.push({
            type: "text",
            x: 3,
            y: currentY + 2,
            text: "✓",
            fontSize: 14,
            strokeColor: "#FFFFFF",
            groupIds: [groupId]
          });
        }
      } else {
        const isNumeric = !!trimmed.match(/^\d+\./);
        if (isNumeric) {
          const numMatch = trimmed.match(/^(\d+\.)/);
          const num = numMatch ? numMatch[1] : "";
          listItemElements.push({
            type: "text",
            x: 0,
            y: currentY,
            text: num,
            fontSize,
            strokeColor: "#666666",
            groupIds: [groupId]
          });
        } else {
          listItemElements.push({
            type: "ellipse",
            x: 6,
            y: currentY + 10,
            width: 6,
            height: 6,
            strokeColor: "#444444",
            backgroundColor: "#444444",
            fillStyle: "solid",
            groupIds: [groupId]
          });
        }
      }

      const format = getFormatInfo(listMatch[4]);

      listItemElements.push({
        type: "text",
        x: xOffset,
        y: currentY,
        width: DEFAULT_WIDTH - xOffset,
        text: text,
        fontSize: fontSize * format.scale,
        strokeColor: isChecked ? "#999999" : format.color,
        textAlign: "left",
        opacity: isChecked ? 60 : 100,
        fontFamily: 1,
        groupIds: [groupId]
      });

      elements.push(...convertToExcalidrawElements(listItemElements as any));
      
      const lines = estimateLines(text, fontSize, DEFAULT_WIDTH - xOffset);
      currentY += (lines * fontSize * 1.5) + 12;
      i++;
      continue;
    }

    // 5. Detect Tables (GFM) - Robust Flattened & Multi-line support
    if (trimmed.includes("|") && (trimmed.includes("---") || trimmed.includes(":---") || (i + 1 < lines.length && lines[i+1].includes("|") && (lines[i+1].includes("---") || lines[i+1].includes(":---"))))) {
      let tableRowsRaw: string[] = [];
      let j = i;
      
      // Check if current line is already a "flattened" full table
      const pipesCount = (trimmed.match(/\|/g) || []).length;
      if (pipesCount > 5 && (trimmed.includes("---") || trimmed.includes(":---"))) {
        // Advanced split: looks for | followed by space(s) and another |
        // and also handles the edge case of multiple rows stuck together
        tableRowsRaw = trimmed.split(/\|\s*\|/).map(l => {
          let s = l.trim();
          if (!s.startsWith("|")) s = "|" + s;
          if (!s.endsWith("|")) s = s + "|";
          return s;
        });
        j = i + 1;
      } else {
        // Standard multi-line detection
        while (j < lines.length && lines[j].trim().includes("|")) {
          tableRowsRaw.push(lines[j].trim());
          j++;
        }
      }

      const tableData: string[][] = [];
      let hasSeparator = false;

      tableRowsRaw.forEach(rowLine => {
        // More lenient separator check: | --- | or |:---| etc.
        if (rowLine.match(/^\|[\s:-|]*\|$/) && rowLine.includes("-")) {
          hasSeparator = true;
        } else {
          const cells = rowLine.split("|").map(c => c.trim()).filter((_, idx, arr) => idx > 0 && idx < arr.length - 1);
          if (cells.length > 0) tableData.push(cells);
        }
      });

      if (tableData.length > 0 && (hasSeparator || tableData.length > 1)) {
        const fontSize = config.fontSize * 0.9;
        const cellPadding = 12;
        const colWidths: number[] = [];
        const MAX_COL_WIDTH = 450;
        const frameId = nanoid();
        const startY = currentY;
        const tableElementIds: string[] = [];

        tableData.forEach((row) => {
          row.forEach((cell, idx) => {
            const estimatedWidth = cell.length * fontSize * 0.6 + cellPadding * 2;
            colWidths[idx] = Math.min(Math.max(colWidths[idx] || 0, estimatedWidth), MAX_COL_WIDTH);
          });
        });
        
        const totalTableWidth = colWidths.reduce((sum, w) => sum + w, 0);

        tableData.forEach((row, rowIndex) => {
          let currentX = 0;
          const isHeader = rowIndex === 0;

          let maxLines = 1;
          row.forEach((cell, colIndex) => {
            const format = getFormatInfo(cell.trim());
            const displayText = cleanMarkdown(cell);
            const availableWidth = colWidths[colIndex] - cellPadding * 2;
            const lines = estimateLines(displayText, fontSize * format.scale, availableWidth);
            if (lines > maxLines) {
              maxLines = lines;
            }
          });

          const rowHeight = maxLines * (fontSize * 1.5) + cellPadding * 2;

          row.forEach((cell, colIndex) => {
            const colWidth = colWidths[colIndex];
            const format = getFormatInfo(cell.trim());
            const displayText = cleanMarkdown(cell);

            const shapeId = nanoid();
            tableElementIds.push(shapeId);

            const availableWidth = colWidth - cellPadding * 2;
            const wrappedText = wrapText(displayText, fontSize * format.scale, availableWidth);

            const cellElement = {
                id: shapeId,
                tableGroupId: frameId, // Use a custom property name so Excalidraw doesn't misinterpret it as a frame
                locked: true,
                type: "rectangle",
                x: currentX,
                y: currentY,
                width: colWidth,
                height: rowHeight,
                strokeColor: isHeader ? "#111111" : format.color,
                backgroundColor: isHeader ? "#f0f0f0" : "transparent",
                fillStyle: "solid",
                strokeWidth: 1,
                roughness: 0,
                label: {
                  tableGroupId: frameId, // Pass through
                  text: wrappedText,
                  fontSize: fontSize * format.scale,
                  strokeColor: isHeader ? "#111111" : format.color,
                  verticalAlign: "middle",
                }
              };

              elements.push(...convertToExcalidrawElements([cellElement as any]));

            currentX += colWidth;
          });
          currentY += rowHeight;
        });
        
        // Add the frame encapsulating the table
        elements.push({
          id: frameId,
          type: "frame",
          children: tableElementIds,
          x: -PADDING, // slight padding for frame
          y: startY - PADDING,
          width: totalTableWidth + PADDING * 2,
          height: (currentY - startY) + PADDING * 2,
          name: "Table",
        });
        
        currentY += PADDING;
        i = j;
        continue;
      }
      // If we got here, it looked like a table but wasn't valid. Move on to avoid infinite loop.
      i++;
      continue;
    }

    // 6. Detect Blockquotes
    if (trimmed.startsWith(">")) {
      let quoteText = "";
      while (i < lines.length && lines[i].trim().startsWith(">")) {
        quoteText += lines[i].trim().replace(/^>\s?/, "") + "\n";
        i++;
      }
      
      const rawText = cleanMarkdown(quoteText.trim());
      const text = wrapMarkdownText(rawText, 70); // Wrap quotes at ~70 characters
      const fontSize = config.fontSize * 0.95;
      const linesCount = estimateLines(text, fontSize, DEFAULT_WIDTH - 30);
      const height = linesCount * fontSize * 1.5;

      elements.push(...convertToExcalidrawElements([
        {
          type: "line",
          x: -12,
          y: currentY,
          width: 0,
          height: height,
          strokeColor: "#FF8800",
          strokeWidth: 3,
          points: [[0, 0], [0, height]],
        },
        {
          type: "text",
          x: 12,
          y: currentY,
          width: DEFAULT_WIDTH - 24,
          text,
          fontSize,
          strokeColor: "#555555",
          textAlign: "left",
          fontFamily: 1,
        }
      ] as any));

      currentY += height + PADDING;
      continue;
    }

    // 6. Default Paragraph
    let paragraphText = "";
    while (i < lines.length && lines[i].trim() && !lines[i].trim().startsWith("#") && !lines[i].trim().startsWith("```") && !lines[i].trim().startsWith(">") && !lines[i].trim().match(/^([-*]|\d+\.)\s/)) {
      paragraphText += lines[i].trim() + " ";
      i++;
    }

    if (paragraphText) {
      const format = getFormatInfo(paragraphText.trim());
      const rawText = cleanMarkdown(paragraphText);
      const text = wrapMarkdownText(rawText, 80); // Wrap at ~80 characters
      const fontSize = config.fontSize * format.scale;
      const estimatedWidth = Math.min(DEFAULT_WIDTH, Math.max(200, rawText.length * fontSize * 0.4));
      
      elements.push(...convertToExcalidrawElements([
        {
          type: "text",
          x: 0,
          y: currentY,
          width: estimatedWidth,
          text,
          fontSize,
          strokeColor: format.color,
          textAlign: "left",
          fontFamily: 1,
        }
      ] as any));

      const linesCount = estimateLines(text, fontSize, estimatedWidth);
      currentY += (linesCount * fontSize * 1.5) + PADDING;
    } else {
      i++;
    }
  }

  const groupMapping = new Map();
  elements.forEach((el: any) => {
    if (el.tableGroupId) {
      groupMapping.set(el.id, el.tableGroupId);
    }
  });

  const finalElements = elements.map((el: any) => {
    const finalEl = { ...el };
    
    // Clear out the dummy property just in case
    delete finalEl.tableGroupId;

    // Restore groupIds for the original element (e.g. rectangle)
    if (groupMapping.has(finalEl.id)) {
      finalEl.groupIds = [groupMapping.get(finalEl.id)];
    }

    // Also inject the exact same groupIds into the bound text element
    if (finalEl.type === "text" && finalEl.containerId) {
      if (groupMapping.has(finalEl.containerId)) {
        finalEl.groupIds = [groupMapping.get(finalEl.containerId)];
        finalEl.locked = true; // Lock the text as well
      }
    }
    
    return finalEl;
  });

  return finalElements.filter((el: any) => el.type !== "frame"); // Remove frames if any existed
};
  
const EMOJI_MAP: Record<string, string> = {
  // Smileys & People
  ":smile:": "😄", ":smiley:": "😃", ":grining:": "😀", ":blush:": "😊", ":wink:": "😉",
  ":heart_eyes:": "😍", ":kissing_heart:": "😘", ":kissing:": "😗", ":kissing_smiling_eyes:": "😙",
  ":kissing_closed_eyes:": "😚", ":yum:": "😋", ":stuck_out_tongue_winking_eye:": "😜",
  ":stuck_out_tongue_closed_eyes:": "😝", ":stuck_out_tongue:": "😛", ":flushed:": "😳",
  ":grin:": "😁", ":pensive:": "😔", ":relieved:": "😌", ":heart:": "❤️", ":sparkles:": "✨",
  ":star:": "⭐", ":star2:": "🌟", ":dizzy:": "💫", ":boom:": "💥", ":anger:": "💢",
  ":sweat_drops:": "💦", ":dash:": "💨", ":skull:": "💀", ":clapping:": "👏", ":raised_hands:": "🙌",
  ":thumbsup:": "👍", ":+1:": "👍", ":thumbsdown:": "👎", ":-1:": "👎", ":ok_hand:": "👌",
  ":punch:": "👊", ":fist:": "✊", ":v:": "✌️", ":wave:": "👋", ":hand:": "✋", ":open_hands:": "👐",
  ":point_up:": "☝️", ":point_down:": "👇", ":point_left:": "👈", ":point_right:": "👉",

  // Activities & Objects
  ":rocket:": "🚀", ":fire:": "🔥", ":zap:": "⚡", ":bulb:": "💡", ":idea:": "💡",
  ":check:": "✅", ":done:": "✅", ":x:": "❌", ":cancel:": "❌", ":warning:": "⚠️",
  ":info:": "ℹ️", ":error:": "🚨", ":todo:": "📝", ":note:": "📝", ":book:": "📖",
  ":link:": "🔗", ":paperclip:": "📎", ":shield:": "🛡️", ":key:": "🔑", ":lock:": "🔒",
  ":unlock:": "🔓", ":bell:": "🔔", ":gift:": "🎁", ":party:": "🎉", ":celebrate:": "🎉",
  ":computer:": "💻", ":laptop:": "💻", ":mobile:": "📱", ":phone:": "📱", ":tool:": "🛠️",
  ":hammer:": "🔨", ":gear:": "⚙️", ":search:": "🔍", ":magnifier:": "🔍", ":money:": "💰",
  ":cloud:": "☁️", ":sunny:": "☀️", ":sun:": "☀️", ":moon:": "🌙", ":earth:": "🌍",

  // Smart Symbols
  "(c)": "©", "(r)": "®", "(tm)": "™", "(p)": "℗", "+-": "±", "->": "→", "<-": "←",
  "==>": "⇒", "<==": "⇐", "<->": "↔", "=>": "⇒", "<=": "≤", ">=": "≥", "=/=": "≠",
  "~~": "≈", "<<": "«", ">>": "»", "...": "…",
};

function wrapMarkdownText(text: string, maxCharsPerLine = 80): string {
  if (!text) return "";
  const words = text.split(" ");
  const lines: string[] = [];
  let currentLine = "";
  for (const word of words) {
    if ((currentLine + word).length > maxCharsPerLine && currentLine.length > 0) {
      lines.push(currentLine.trim());
      currentLine = word + " ";
    } else {
      currentLine += word + " ";
    }
  }
  if (currentLine.trim()) {
    lines.push(currentLine.trim());
  }
  return lines.join("\n");
}

function cleanMarkdown(text: string | null | undefined): string {
  if (!text) return "";
  const str = String(text);
  // Pattern for emojis and smart symbols
  const emojiRegex = /:[a-z0-9_+]+:|(\([crp]\))|(\(tm\))|(\+\-) |(->)|(<-)|(==>)|(<==)|(<->)|(=>)|(<=)|(>=)|(=\/=)|(~~)|(<<)|(>>)|(\.\.\.)/g;
  
  let cleaned = str.replace(emojiRegex, (match) => {
    return EMOJI_MAP[match] || match;
  });

  // Strip link markers but keep text (Rich text formatter handles bold/italic/etc)
  cleaned = cleaned.replace(/\[(.*?)\]\(.*?\)/g, "$1");
  cleaned = cleaned.replace(/`(.*?)`/g, "$1");

  return applyRichTextFormatting(cleaned);
}

/**
 * Detects the visual "weight" of a markdown line based on its markers.
 * Returns a scale factor for font size and a flag for stroke color.
 */
function getFormatInfo(text: string | null | undefined): { scale: number; color: string } {
  if (!text) return { scale: 1, color: "#222222" };
  const str = String(text).trim();
  if (str.startsWith("***") && str.endsWith("***")) return { scale: 1.4, color: "#000000" };
  if (str.startsWith("**") && str.endsWith("**")) return { scale: 1.25, color: "#000000" };
  if (str.startsWith("*") && str.endsWith("*")) return { scale: 1.1, color: "#111111" };
  return { scale: 1, color: "#222222" };
}

function estimateLines(text: string | null | undefined, fontSize: number, width: number): number {
  if (!text) return 0;
  const str = String(text);
  const fSize = fontSize || 16;
  const w = width || 200;
  const charsPerLine = Math.max(1, Math.floor(w / (fSize * 0.5)));
  const paragraphs = str.split("\n");
  let totalLines = 0;
  paragraphs.forEach((p) => {
    totalLines += Math.max(1, Math.ceil(p.length / charsPerLine));
  });
  return totalLines;
}
